import { useFormContext, useFormState } from "react-hook-form";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import StyledModal from "@/components/styled/StyledModal";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import { FC } from "react";
import StyledInput from "@/components/styled/StyledInput";
import StyledRadio from "@/components/styled/StyledRadio";
import StyledRadioGroup from "@/components/styled/StyledRadioGroup";
import InfoCircleIcon from "@/assets/icons/info-circle.svg?react";
import usePriceDetail from "./PriceDetailCard/usePriceDetail";
import StyledButton from "@/components/styled/StyledButton";
import StyledInputDate from "@/components/styled/StyledInputDate";
import { addDays } from "date-fns";
import { MIN_DAYS_DEFERRED_SHIPMENT } from "@/constants/miscConstants";
import StyledSpinner from "@/components/styled/StyledSpinner";
import clsx from "clsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CircleQuestionMark } from "lucide-react";

interface SaveCotationModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: () => void;
  isPending: boolean;
}

const SaveCotationModal: FC<SaveCotationModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isPending,
}) => {
  const { control, watch } = useFormContext<CotationFormData>();
  const { errors } = useFormState({ control });
  const translateMessage = useTranslateMessage();

  const { numberOfShipments } = usePriceDetail();

  const express = watch("express");
  const deliveryDateRadio = watch("deliveryDateRadio");

  return (
    <StyledModal open={open} handleClose={handleClose} title="Enregistrer la cotation">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          <span className="font-semibold text-blue-600">Informations du client</span>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs font-medium tracking-wide">
                Particulier
              </span>
              <div className="md-gap-4 flex flex-col gap-2 md:flex-row">
                <StyledInput title="Nom" control={control} name="customer.lastName" />
                <StyledInput title="Prénom" control={control} name="customer.firstName" />
              </div>
            </div>
            <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="bg-background text-muted-foreground relative z-10 px-2">Ou</span>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs font-medium tracking-wide">
                Professionnel
              </span>
              <StyledInput
                title="Nom de la société"
                control={control}
                name="customer.companyName"
              />
            </div>
            {errors.customer?.root?.message && (
              <span className="text-sm text-red-500">
                {translateMessage(errors.customer.root.message)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row items-center gap-4">
            <span className="font-semibold text-blue-600">{"Date d'enlèvement"}</span>
            {!express && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="hover:bg-transparent">
                    <CircleQuestionMark className="size-4 text-gray-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px]">
                  <p className="text-base font-semibold">Le plus tôt possible</p>
                  <p className="text-sm">
                    Selon les jours de collecte de votre zone (livraison estimée sous 5 à 12 jours
                    ouvrés). Vous pourrez modifier cette date ultérieurement dans votre onglet «
                    Validées ».
                  </p>
                  <p className="text-base font-semibold">Livraison à partir de</p>
                  <p className="text-sm">
                    Choisissez à partir de quelle date la livraison doit être effective ;
                    l’enlèvement se placera automatiquement quelques jours avant dans votre onglet «
                    Validées »
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <StyledRadioGroup
              name="deliveryDateRadio"
              control={control}
              defaultValue="earliestDelivery"
            >
              {express ? (
                <div
                  className={clsx(
                    "flex flex-col gap-2 rounded-lg border p-2",
                    deliveryDateRadio === "earliestDelivery" && "border-blue-500 bg-blue-50",
                    deliveryDateRadio === "customDelivery" && "border-gray-200 bg-gray-50",
                  )}
                >
                  <StyledRadio
                    label="Livraison 24h à 48h (hors délai de douane)"
                    value="earliestDelivery"
                    disabled
                    checked={deliveryDateRadio === "earliestDelivery"}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div
                    className={clsx(
                      "flex flex-col gap-2 rounded-lg border p-2",
                      deliveryDateRadio === "earliestDelivery" && "border-blue-500 bg-blue-50",
                      deliveryDateRadio === "customDelivery" && "border-gray-200 bg-gray-50",
                    )}
                  >
                    <StyledRadio
                      label="Le plus tôt possible"
                      value="earliestDelivery"
                      checked={deliveryDateRadio === "earliestDelivery"}
                    />
                  </div>
                  <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                    <span className="bg-background text-muted-foreground relative z-10 px-2">
                      Ou
                    </span>
                  </div>
                  <div
                    className={clsx(
                      "flex flex-row items-center gap-2 rounded-lg border p-2",
                      deliveryDateRadio === "customDelivery" && "border-blue-500 bg-blue-50",
                      deliveryDateRadio === "earliestDelivery" && "border-gray-200 bg-gray-50",
                    )}
                  >
                    <StyledRadio
                      label="Livraison à partir de"
                      value="customDelivery"
                      checked={deliveryDateRadio === "customDelivery"}
                    />
                    <StyledInputDate
                      control={control}
                      name="customDeliveryDate"
                      defaultValue={addDays(new Date(), MIN_DAYS_DEFERRED_SHIPMENT)}
                      minDate={addDays(new Date(), MIN_DAYS_DEFERRED_SHIPMENT)}
                    />
                  </div>
                </div>
              )}
            </StyledRadioGroup>
          </div>
        </div>
        {numberOfShipments && numberOfShipments > 1 && (
          <div className="flex flex-row gap-4 rounded-lg border-2 border-orange-300 bg-orange-50 p-4">
            <InfoCircleIcon className="text-orange-700" />
            <div>
              <p className="font-semibold">
                Votre commande sera expédiée en {numberOfShipments} fois.
              </p>
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <StyledButton mode="quaternary" onClick={handleClose}>
            Annuler
          </StyledButton>
          <StyledButton disabled={isPending} onClick={onSubmit}>
            {isPending ? <StyledSpinner /> : "Enregistrer"}
          </StyledButton>
        </div>
      </div>
    </StyledModal>
  );
};

export default SaveCotationModal;
